import {
    format,
    ISO_DATE_ONLY_FORMAT,
    zodDateOfBirthValidation,
    zodFirstNameValidation,
    zodLastNameValidation,
} from '@fruitful/fruitils';
import {
    AnalyticsMembershipEvent,
    AnalyticsUserEvent,
    PersistedUserEvent,
    type PersistedUserEventSmsConsentMetadata,
} from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFeatureGate } from '@statsig/expo-bindings';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Platform } from 'react-native';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Checkbox,
    CloseButton,
    ControlledDateTextInput,
    ControlledInput,
    isNonMobileWeb,
    UserPlusIcon,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';
import z from 'zod';

import BackgroundImage from '../../../../assets/images/backgrounds/orchard.png';
import {
    GeneralDisclosuresFootnote,
    InvestmentManagementAgreementDisclosure,
    POLICY_DISCLOSURES,
    PolicyType,
} from '../../../banking/components/Disclosures';
import { isEmpty } from '../../../common/misc';
import { useStorage } from '../../../common/providers/StorageProvider';
import {
    formatWithMask,
    PHONE_NUMBER_MASK,
    VALID_PHONE_NUMBER_MASK,
} from '../../../common/utils/maskUtils';
import { useLogUserEvent } from '../../../events/hooks/useLogUserEvent';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import {
    captureException,
    captureWarning,
    identifyUser,
    track,
    trackForMembership,
} from '../../../logger/Logger';
import {
    SMS_OPT_IN_DISCLOSURE_HEADLINE,
    SMS_OPT_IN_DISCLOSURE_TEXT,
} from '../../../notifications/smsConsentDisclosure';
import { SUPPORT_EMAIL } from '../../../settings/types/constants';
import { useUser } from '../../../user/providers/UserProvider';
import { useVisitorId } from '../../../user/providers/VisitorProvider';
import { OnboardingStatus, type User } from '../../../user/types/User';
import { fetchDeviceToken } from '../../api/AuthApi';
import { isUserFirstSession, storeDeviceToken } from '../../helpers';
import { useCompleteMemberSignup } from '../../hooks/start-membership/useCompleteMemberSignup';
import { useCreateMember } from '../../hooks/start-membership/useCreateMember';
import { useCreatePartnerMember } from '../../hooks/start-membership/useCreatePartnerMember';
import { useCreateStytchAccount } from '../../hooks/start-membership/useCreateStytchAccount';
import { useStytchSession, useStytchUser } from '../../hooks/useAuth';
import { isUserSetupInProgressAtom } from '../../state/AuthAtoms';
import { PasswordStrengthChecker } from '../PasswordStrengthChecker';
import ShowPasswordToggle from '../ShowPasswordToggle';
import { shouldShowIOSAppPage } from './GetTheApp';
import { SplitScreenHighlightLayout } from './SplitScreenHighlightLayout';
import { StartMembershipTestIds } from './StartMembershipTestIds';

const registerDeviceToken = async (
    stytchUserId: string,
    stytchSessionId: string,
) => {
    // This endpoint currently depends on having a fruitful user account
    const deviceToken = await fetchDeviceToken({
        stytchId: stytchUserId,
        sessionId: stytchSessionId,
        isFirstLogin: true,
    });

    if (deviceToken == null) {
        captureWarning('unable to verify device with jwt');
    } else {
        storeDeviceToken(deviceToken);
    }
};

const baseSchema = z.object({
    email: z.string().email('Please enter a valid email address.'),
    password: z.string().min(1, 'Please enter a password.'),
    // We have these to represent a 'already submitted' state of the form to the user in disabled inputs.
    // The password should not actually be their password, but just a placeholder string.
    readonlyEmail: z.string().optional(),
    readonlyPassword: z.string().optional(),
    firstName: zodFirstNameValidation,
    lastName: zodLastNameValidation,
    phoneNumber: z
        .string()
        .min(10, 'Please enter a valid phone number.')
        .regex(VALID_PHONE_NUMBER_MASK, 'Please enter a valid phone number'),
    dateOfBirth: zodDateOfBirthValidation,
});

const schemaWithoutPartner = z
    .object({
        hasPartner: z.literal(false),
    })
    .merge(baseSchema);

const schemaWithPartner = z
    .object({
        hasPartner: z.literal(true),
        partnerEmail: z
            .string()
            .email("Please provide your partner's email address."),
        partnerFirstName: zodFirstNameValidation,
        partnerLastName: zodLastNameValidation,
    })
    .merge(baseSchema);

const startMembershipSchema = z.discriminatedUnion('hasPartner', [
    schemaWithoutPartner,
    schemaWithPartner,
]);

// Setting the full type definition here manually because z.infer<typeof startMembershipSchema> wasn't working as expected
type StartMembershipSchema = z.infer<typeof baseSchema> & {
    hasPartner: boolean;
    partnerEmail: string;
    partnerFirstName: string;
    partnerLastName: string;
};

export const StartMembership = () => {
    const router = useRouter();
    const toast = useToastController();
    const { user, refetch: refetchUser } = useUser();
    const { user: stytchUser } = useStytchUser();
    const { session: stytchSession } = useStytchSession();
    const setIsUserSetupInProgress = useSetAtom(isUserSetupInProgressAtom);
    const storage = useStorage();
    const visitorId = useVisitorId();
    const { trackViewOnce } = useViewEvents();

    const {
        customerId: customerIdParam,
        email: emailParam,
        guideSignUpId: guideSignUpIdParam,
        plan: planParam,
        referralCode: referralCodeParam,
    } = useLocalSearchParams<{
        customerId?: string;
        email?: string;
        guideSignUpId?: string;
        plan?: string;
        referralCode?: string;
    }>();

    // TODO error logging to check these values/types
    const stripeCustomerId = customerIdParam as string;
    const signupEmail =
        user?.email ?? stytchUser?.emails[0]?.email ?? (emailParam as string);
    const guideSignUpId = guideSignUpIdParam as string;
    const membershipType = planParam as string;

    const [showPassword, setShowPassword] = useState(false);
    const [authError, setAuthError] = useState<Error | null>(null);
    const [showPartnerSection, setShowPartnerSection] =
        useState<boolean>(false);
    const [smsConsentChecked, setSmsConsentChecked] = useState(false);
    const { masked: formattedPhoneNumber, unmasked } = formatWithMask(
        PHONE_NUMBER_MASK,
        user?.phoneNumber != null ? user.phoneNumber.substring(2) : '', // Remove country code (+1)
    );
    const [unmaskedPhoneNumber, setUnmaskedPhoneNumber] = useState(unmasked);

    const { value: enableGuidanceSessionReminderSMS } = useFeatureGate(
        FeatureFlags.ENABLE_GUIDANCE_SESSION_REMINDER_SMS,
    );

    useEffect(() => {
        const email = (emailParam ?? '').toLowerCase();
        if (!isEmpty(email)) {
            const key = `user.${email}.signup.subscriptionStartedPublished`;
            const hasEventBeenPublished = storage.getBool(key);
            if (!hasEventBeenPublished) {
                track(AnalyticsUserEvent.SIGNUP_FUNNEL_SUBSCRIPTION_STARTED, {
                    email,
                    stripeCustomerId,
                    guideSignUpId,
                    membershipType,
                    eventContext:
                        'published after successful checkout when "StartMembership" page is loaded',
                });
                storage.setBool(key, true);
            }
        }
        // other dependencies not included as this the important code will only run once after email is populated
    }, [emailParam]);

    useEffect(() => {
        // redirect away if onboarding has been completed or user does not exist
        if (user?.isFullyAuthed && !isUserFirstSession()) {
            if (
                [
                    OnboardingStatus.WELCOME_CALL,
                    OnboardingStatus.COMPLETED,
                    OnboardingStatus.JOINT_COMPLETED,
                ].includes(user.onboardingStatus)
            ) {
                router.push('/home');
            }
        }
    }, [user]);

    const form = useForm<StartMembershipSchema>({
        resolver: zodResolver(startMembershipSchema),
        defaultValues: {
            // TODO read default email and name from local storage
            email: signupEmail ?? '',
            password: '',
            readonlyEmail: signupEmail ?? '',
            readonlyPassword: '****************',
            firstName: user?.firstName ?? '',
            lastName: user?.lastName ?? '',
            dateOfBirth: '',
            phoneNumber: formattedPhoneNumber,
            hasPartner: false,
            partnerEmail: '',
            partnerFirstName: '',
            partnerLastName: '',
        },
    });

    useEffect(() => {
        form.setValue('hasPartner', showPartnerSection);
    }, [showPartnerSection]);

    const watchedPassword = form.watch('password');

    const onPasswordStrengthChange = useCallback(() => {
        setAuthError(null);
    }, []);

    const { createStytchAccount, isCreatingStytchAccount } =
        useCreateStytchAccount();
    const { createMember, isCreatingMember } = useCreateMember();
    const { createPartnerMember, isCreatingPartnerMember } =
        useCreatePartnerMember();
    const { completeMemberSignup, isCompletingMemberSignup } =
        useCompleteMemberSignup();
    const { logUserEvent } = useLogUserEvent();

    // TODO: should be local storage, not state? Consider page refreshes when only partially complete
    const isMemberCreationComplete = useRef(false);
    const isPartnerMemberCreationComplete = useRef(false);
    const isUserSignupCompletionHandled = useRef(false);

    const continueToNextPage = useCallback(() => {
        if (shouldShowIOSAppPage()) {
            router.push('/start-membership/get-the-app');
        } else {
            router.push('/home');
        }
    }, [router]);

    const onAllCreationComplete = useCallback(
        async (userId: string) => {
            setIsUserSetupInProgress(false);
            if (!isUserSignupCompletionHandled.current) {
                // TODO: also need to log membership purchase published for signup funnel events
                await completeMemberSignup({ userId });
                isUserSignupCompletionHandled.current = true;
                logUserEvent(
                    PersistedUserEvent.DISCLOSURE_ACKNOWLEDGEMENT_INVESTMENT_MANAGEMENT_AGREEMENT,
                    POLICY_DISCLOSURES[
                        PolicyType.INVESTMENT_MANAGEMENT_AGREEMENT
                    ],
                );
                if (smsConsentChecked) {
                    const metadata: PersistedUserEventSmsConsentMetadata = {
                        phoneNumber: `+1${unmaskedPhoneNumber}`,
                        disclosureText: `${SMS_OPT_IN_DISCLOSURE_HEADLINE} ${SMS_OPT_IN_DISCLOSURE_TEXT}`,
                        platform: Platform.OS,
                    };

                    logUserEvent(
                        PersistedUserEvent.SMS_CONSENT_OPT_IN,
                        metadata,
                    );
                }
            }
            continueToNextPage();
        },
        [continueToNextPage, smsConsentChecked, unmaskedPhoneNumber],
    );

    const onCreatePartnerMemberSuccess = useCallback(
        async (userId: string) => {
            if (!isPartnerMemberCreationComplete.current) {
                isPartnerMemberCreationComplete.current = true;
                trackForMembership(
                    AnalyticsMembershipEvent.SIGNUP_FUNNEL_PARTNER_MEMBER_CREATED,
                );
                if (refetchUser) await refetchUser();
            }
            void onAllCreationComplete(userId);
        },
        [onAllCreationComplete],
    );

    const onCreatePartnerMemberError = useCallback(
        (e: Error) => {
            isPartnerMemberCreationComplete.current = false;
            const isConflictError = e.message === 'Conflict Exception';
            const errorMessage: string = isConflictError
                ? `Your partner's email address is already in our system. Please reach out to ${SUPPORT_EMAIL} if you need assistance.`
                : `Something went wrong. Please try again, or reach out to ${SUPPORT_EMAIL} for assistance`;
            captureException(e, {
                data: 'Error during partner member invitation',
            });
            if (isConflictError) {
                form.setError('partnerEmail', {
                    type: 'manual',
                    message: errorMessage,
                });
            } else {
                setAuthError(new Error(errorMessage));
            }
            setIsUserSetupInProgress(false);
        },
        [form],
    );

    const onCreateMemberSuccess = useCallback(
        async (user: User, stytchId: string, stytchSessionId: string) => {
            if (!isMemberCreationComplete.current) {
                isMemberCreationComplete.current = true;
                identifyUser(user);
                track(AnalyticsUserEvent.SIGNUP_FUNNEL_ACCOUNT_CREATED);
            }

            await registerDeviceToken(stytchId, stytchSessionId);

            const {
                hasPartner,
                partnerEmail,
                partnerFirstName,
                partnerLastName,
            } = form.getValues();
            if (
                hasPartner &&
                partnerEmail != null &&
                partnerFirstName != null &&
                partnerLastName != null &&
                user.partnerMember == null &&
                !isPartnerMemberCreationComplete.current
            ) {
                createPartnerMember({
                    userId: user.id,
                    partnerEmail,
                    partnerFirstName,
                    partnerLastName,
                    onSuccess: onCreatePartnerMemberSuccess,
                    onError: onCreatePartnerMemberError,
                });
            } else {
                void onAllCreationComplete(user.id);
            }
        },
        [
            stytchUser,
            stytchSession,
            form,
            onAllCreationComplete,
            onCreatePartnerMemberSuccess,
            onCreatePartnerMemberError,
        ],
    );

    const onCreateMemberError = useCallback((e: Error) => {
        isMemberCreationComplete.current = false;
        captureException(e, {
            data: 'Error during member creation',
        });
        toast.show('Something went wrong, please try again');
        setIsUserSetupInProgress(false);
    }, []);

    const onCreateStytchAccountSuccess = useCallback(
        (stytchId: string, stytchEmail: string, stytchSessionId: string) => {
            track(AnalyticsUserEvent.SIGNUP_FUNNEL_ACCOUNT_SUBMIT);

            form.setValue('readonlyEmail', stytchEmail, {
                shouldTouch: true,
            });
            form.setValue('readonlyPassword', '****************', {
                shouldTouch: true,
            });

            // Skip ahead if the part below is already done
            if (user?.stytchId === stytchId) {
                void onCreateMemberSuccess(user, stytchId, stytchSessionId);
                return;
            }

            const { email, firstName, lastName, dateOfBirth } =
                form.getValues();
            createMember({
                customerId: stripeCustomerId,
                email,
                stytchId,
                firstName,
                lastName,
                dateOfBirth: `${format(dateOfBirth, ISO_DATE_ONLY_FORMAT)}T00:00:00Z`, // Format with timestamp for backend call
                phoneNumber: `+1${unmaskedPhoneNumber}`,
                guideSignUpId,
                ...((referralCodeParam ?? '') !== ''
                    ? { referralCode: referralCodeParam }
                    : {}),
                visitorId,
                onSuccess: (user: User) => {
                    void onCreateMemberSuccess(user, stytchId, stytchSessionId);
                },
                onError: onCreateMemberError,
            });
        },
        [
            form,
            user,
            unmaskedPhoneNumber,
            onCreateMemberSuccess,
            onCreateMemberError,
        ],
    );

    const onCreateStytchAccountError = useCallback((error: Error) => {
        form.setError('root', {
            type: 'manual',
            message: error.message,
        });
        setAuthError(error);
        setIsUserSetupInProgress(false);
    }, []);

    const onFormSubmit = useCallback(
        (formData: StartMembershipSchema) => {
            setAuthError(null);
            setIsUserSetupInProgress(true);

            const { email, password } = formData;

            //  Skip ahead if the submission is partially complete
            if (stytchUser && stytchSession) {
                onCreateStytchAccountSuccess(
                    stytchUser.user_id,
                    stytchUser.emails[0]?.email ?? '',
                    stytchSession.session_id,
                );
                return;
            }

            void createStytchAccount({
                email,
                password,
                onSuccess: onCreateStytchAccountSuccess,
                onError: onCreateStytchAccountError,
            });
        },
        [
            createStytchAccount,
            onCreateStytchAccountSuccess,
            onCreateStytchAccountError,
            stytchUser,
            stytchSession,
        ],
    );

    trackViewOnce('StartMembership');

    const isSubmitting =
        isCreatingStytchAccount ||
        isCreatingMember ||
        isCreatingPartnerMember ||
        isCompletingMemberSignup;

    const title = 'Welcome to Fruitful';
    const subtitle =
        'Start building your profile to schedule your first session.';
    const DISCLOSURE_ACKNOWLEDGEMENT_BUTTON_LABEL = 'Agree and continue';

    const phoneNumberInput = (
        <ControlledInput
            label="Phone number"
            name="phoneNumber"
            control={form.control}
            register={form.register}
            errors={form.formState.errors}
            formatValueOnChange={(_, value) => {
                const { masked, unmasked } = formatWithMask(
                    PHONE_NUMBER_MASK,
                    value,
                );
                setUnmaskedPhoneNumber(unmasked);
                return masked;
            }}
            placeholder="(123) 456-7890"
            autoComplete="tel-national"
            inputMode="tel"
            keyboardType="numbers-and-punctuation"
            disabled={isSubmitting || !isEmpty(user?.phoneNumber)}
        />
    );

    return (
        <SplitScreenHighlightLayout
            backgroundImage={BackgroundImage}
            title={title}
            subtitle={subtitle}
            includeLogo
            disclosures={<Disclosures />}
            scrollViewTestID={StartMembershipTestIds.mainScrollView}
        >
            <YStack testID={StartMembershipTestIds.page} width="100%" gap={24}>
                <YStack width="100%" gap={16}>
                    {watchedPassword.length > 0 &&
                        authError?.message != null &&
                        authError?.message.length > 0 && (
                            <BodySmallRegular color="$red1100">
                                {authError?.message}
                            </BodySmallRegular>
                        )}
                    {user?.email != null ? (
                        <ControlledInput
                            testID={StartMembershipTestIds.readonlyEmailInput}
                            readOnly={true}
                            disabled={true}
                            label="Email address"
                            name="readonlyEmail"
                            inputMode="email"
                            keyboardType="email-address"
                            textContentType="username"
                            control={form.control}
                            register={form.register}
                            errors={form.formState.errors}
                        />
                    ) : (
                        <ControlledInput
                            testID={StartMembershipTestIds.emailInput}
                            label="Email address"
                            name="email"
                            placeholder="Your personal email address"
                            autoComplete="email"
                            autoCapitalize="none"
                            inputMode="email"
                            keyboardType="email-address"
                            textContentType="username"
                            control={form.control}
                            register={form.register}
                            errors={form.formState.errors}
                            onSubmitEditing={() => {
                                form.setFocus('password');
                            }}
                            // since this is the purpose of the page, autofocus seems safe
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus={isNonMobileWeb}
                            disabled={isSubmitting}
                            readOnly={isSubmitting}
                        />
                    )}
                    {isSubmitting || user?.email != null ? (
                        <ControlledInput
                            testID={
                                StartMembershipTestIds.readonlyPasswordInput
                            }
                            readOnly={true}
                            disabled={true}
                            label="Password"
                            name="readonlyPassword"
                            inputMode="text"
                            keyboardType="default"
                            textContentType="password"
                            secureTextEntry={true}
                            control={form.control}
                            register={form.register}
                            errors={form.formState.errors}
                        />
                    ) : (
                        <View>
                            <ControlledInput
                                testID={StartMembershipTestIds.passwordInput}
                                label="Password"
                                name="password"
                                autoCapitalize="none"
                                autoComplete="new-password"
                                placeholder="Set your password"
                                inputMode="text"
                                keyboardType="default"
                                textContentType="password"
                                secureTextEntry={!showPassword}
                                control={form.control}
                                register={form.register}
                                errors={form.formState.errors}
                                onSubmitEditing={() => {
                                    form.setFocus('firstName');
                                }}
                                rightSideContent={
                                    <ShowPasswordToggle
                                        showPassword={showPassword}
                                        setShowPassword={setShowPassword}
                                    />
                                }
                            />
                            <PasswordStrengthChecker
                                password={watchedPassword}
                                onStrengthCheckChange={onPasswordStrengthChange}
                            />
                        </View>
                    )}
                    <XStack gap={12}>
                        <ControlledInput
                            containerStyle={{ flex: 1 }}
                            label="First name"
                            name="firstName"
                            placeholder="Your first name"
                            control={form.control}
                            register={form.register}
                            errors={form.formState.errors}
                            autoCapitalize="words"
                            autoComplete="name-given"
                            textContentType="name"
                            enterKeyHint="next"
                            onSubmitEditing={() => {
                                form.setFocus('lastName');
                            }}
                            disabled={isSubmitting || !isEmpty(user?.firstName)}
                        />
                        <ControlledInput
                            containerStyle={{ flex: 1 }}
                            label="Last name"
                            name="lastName"
                            placeholder="Your last name"
                            control={form.control}
                            register={form.register}
                            errors={form.formState.errors}
                            autoCapitalize="words"
                            autoComplete="name-family"
                            textContentType="name"
                            enterKeyHint="next"
                            onSubmitEditing={() => {
                                form.setFocus('dateOfBirth');
                            }}
                            disabled={isSubmitting || !isEmpty(user?.lastName)}
                        />
                    </XStack>
                    <ControlledDateTextInput
                        label="Date of birth"
                        name="dateOfBirth"
                        control={form.control}
                        register={form.register}
                        errors={form.formState.errors}
                        onSubmitEditing={() => {
                            form.setFocus('phoneNumber');
                        }}
                        disabled={isSubmitting}
                    />
                    {enableGuidanceSessionReminderSMS ? (
                        <YStack gap={8}>
                            {phoneNumberInput}
                            <BodySmallRegular
                                color="$gray1000"
                                fontStyle="italic"
                                paddingHorizontal={4}
                            >
                                May be used for appointment reminders.
                            </BodySmallRegular>
                            <XStack
                                alignItems="flex-start"
                                gap={10}
                                paddingHorizontal={4}
                                paddingTop={4}
                            >
                                <Checkbox
                                    id="sms-consent"
                                    checked={smsConsentChecked}
                                    onCheckedChange={(checked) => {
                                        setSmsConsentChecked(checked === true);
                                    }}
                                    disabled={isSubmitting}
                                    marginTop={-1}
                                />
                                <YStack flex={1} gap={6}>
                                    <BodySmallRegular
                                        cursor="pointer"
                                        onPress={() => {
                                            setSmsConsentChecked(
                                                (prev) => !prev,
                                            );
                                        }}
                                    >
                                        {SMS_OPT_IN_DISCLOSURE_HEADLINE}
                                    </BodySmallRegular>
                                    <BodySmallRegular color="$gray1000">
                                        {SMS_OPT_IN_DISCLOSURE_TEXT}
                                    </BodySmallRegular>
                                </YStack>
                            </XStack>
                        </YStack>
                    ) : (
                        phoneNumberInput
                    )}
                </YStack>
                {showPartnerSection ? (
                    <YStack
                        testID={StartMembershipTestIds.partnerPanel}
                        width="100%"
                        padding={16}
                        borderColor="$gray600"
                        borderWidth={1}
                        borderRadius={16}
                        gap={12}
                    >
                        <XStack
                            justifyContent="space-between"
                            alignItems="center"
                            marginBottom={12}
                            marginRight={-4}
                        >
                            <BodyMediumRegular>
                                Partner details
                            </BodyMediumRegular>
                            {!isSubmitting && (
                                <CloseButton
                                    testID={
                                        StartMembershipTestIds.partnerCloseButton
                                    }
                                    backgroundColor="$backgroundTransparent"
                                    onPress={() => {
                                        setShowPartnerSection(false);
                                    }}
                                />
                            )}
                        </XStack>
                        <XStack gap={12}>
                            <ControlledInput
                                disabled={isSubmitting}
                                containerStyle={{ flex: 1 }}
                                label="Partner's first name"
                                name="partnerFirstName"
                                placeholder="Their first name"
                                control={form.control}
                                register={form.register}
                                errors={form.formState.errors}
                                autoCapitalize="words"
                                enterKeyHint="next"
                                onSubmitEditing={() => {
                                    form.setFocus('partnerLastName');
                                }}
                            />
                            <ControlledInput
                                disabled={isSubmitting}
                                containerStyle={{ flex: 1 }}
                                label="Partner's last name"
                                name="partnerLastName"
                                placeholder="Their last name"
                                control={form.control}
                                register={form.register}
                                errors={form.formState.errors}
                                autoCapitalize="words"
                                enterKeyHint="next"
                                onSubmitEditing={() => {
                                    form.setFocus('partnerEmail');
                                }}
                            />
                        </XStack>
                        <YStack gap={4}>
                            <ControlledInput
                                disabled={isSubmitting}
                                label="Partner's email address"
                                name="partnerEmail"
                                placeholder="Their email address"
                                autoCapitalize="none"
                                inputMode="email"
                                keyboardType="email-address"
                                control={form.control}
                                register={form.register}
                                errors={form.formState.errors}
                                // @ts-expect-error We don't want autoComplete set to 'email' to avoid autofill of the wrong email so using something the browser is unfamiliar with should prevent autofill
                                autoComplete="partner-email"
                            />
                            <BodySmallRegular color="$gray1000">
                                We&apos;ll send your partner an email to get
                                their account set up.
                            </BodySmallRegular>
                        </YStack>
                    </YStack>
                ) : (
                    <YStack
                        backgroundColor="$green100"
                        borderColor="$green500"
                        borderWidth={1}
                        borderRadius={16}
                        paddingVertical={16}
                        paddingHorizontal={isNonMobileWeb ? 24 : 16}
                        gap={isNonMobileWeb ? 24 : 16}
                        flexDirection={isNonMobileWeb ? 'row' : 'column'}
                    >
                        <View display="inline" flex={1}>
                            <BodyMediumEmphasized display="inline">
                                Sharing finances?&nbsp;
                            </BodyMediumEmphasized>
                            <BodyMediumRegular display="inline">
                                Add your partner now for a smoother setup —
                                it&apos;s free and easier to manage together.
                            </BodyMediumRegular>
                        </View>
                        <Button
                            testID={StartMembershipTestIds.addPartnerButton}
                            disabled={isSubmitting}
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            width={isNonMobileWeb ? 'auto' : 'full'}
                            tone="positive"
                            text="Add partner"
                            icon={UserPlusIcon}
                            onPress={() => {
                                setShowPartnerSection(true);
                            }}
                        />
                    </YStack>
                )}
                <View paddingHorizontal={4}>
                    <InvestmentManagementAgreementDisclosure
                        acknowledgementButtonLabel={
                            DISCLOSURE_ACKNOWLEDGEMENT_BUTTON_LABEL
                        }
                    />
                </View>
                <Button
                    testID={StartMembershipTestIds.continueButton}
                    disabled={isSubmitting}
                    variant={ButtonVariant.PRIMARY}
                    tone="positive"
                    width="full"
                    onPress={form.handleSubmit(onFormSubmit)}
                    text={DISCLOSURE_ACKNOWLEDGEMENT_BUTTON_LABEL}
                />
            </YStack>
        </SplitScreenHighlightLayout>
    );
};

const Disclosures = () => {
    return (
        <View
            paddingTop={32}
            width="98%"
            maxWidth={600}
            alignSelf="center"
            userSelect="none"
            $sm={{ paddingTop: 64 }}
            $lg={{ paddingTop: 24 }}
        >
            <GeneralDisclosuresFootnote />
        </View>
    );
};
